import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import ImageUpload from '../image-editing/ImageUpload';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import { ModelTemplate, BackgroundPreset, PosePreset, CataloguePoseData, BACKGROUND_CATEGORIES } from './types';
import { useWorkflowData } from './hooks/useWorkflowData';

// Modular Components
import { WorkflowStepper } from './components/WorkflowStepper';
import { ModelGrid } from './components/ModelGrid';
import { PoseGrid } from './components/PoseGrid';
import { BackgroundGrid } from './components/BackgroundGrid';
import { GeneratedResults } from './components/GeneratedResults';
import { SummarySidebar } from './components/SummarySidebar';

type Step = 'SELECT_PRODUCTS' | 'SELECT_MODELS' | 'SELECT_POSES' | 'SELECT_BACKGROUND' | 'SUMMARY';

/** Config for workflows that share the same flow: front+back (or front-only) → models → poses → background → summary */
export interface MultiModelPoseWorkflowConfig {
  /** Sent to API in credit-cost and generate (e.g. 'catalogue-generator', 'stitched-cloths') */
  workflowType: string;
  /** Display name in UI (e.g. 'Catalogue Generator', 'Stitched Cloths') */
  workflowName: string;
  /** If true, both front and back images required; if false, only front required */
  backImageRequired: boolean;
  /** Label for the back image upload (e.g. 'Back Image' or 'Back Image (Optional)') */
  backImageLabel: string;
  /** Prefix for result download filenames (e.g. 'catalogue', 'stitched') */
  downloadFilenamePrefix: string;
}

interface MultiModelPoseWorkflowBaseProps {
  onBackToWorkflows: () => void;
  initialInputImages?: string[];
  initialGeneratedImages?: string[];
  initialModelImageUrl?: string;
  initialBackgroundImageUrl?: string;
  config: MultiModelPoseWorkflowConfig;
}

const MultiModelPoseWorkflowBase: React.FC<MultiModelPoseWorkflowBaseProps> = ({ onBackToWorkflows, initialInputImages, initialGeneratedImages, initialModelImageUrl, initialBackgroundImageUrl, config }) => {
  const { workflowType, workflowName, backImageRequired, backImageLabel, downloadFilenamePrefix } = config;
  const navigate = useNavigate();
  const { currentUser, refreshUserCredits, userCredits } = useAuth();

  const [currentStep, setCurrentStep] = useState<Step>(initialGeneratedImages?.length ? 'SUMMARY' : 'SELECT_PRODUCTS');
  const [frontImageUrl, setFrontImageUrl] = useState(initialInputImages?.[0] ?? '');
  const [backImageUrl, setBackImageUrl] = useState(initialInputImages?.[1] ?? '');
  const [generatedImages, setGeneratedImages] = useState<string[]>(initialGeneratedImages || []);
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('performance');
  const [selectedCatalogueModelIds, setSelectedCatalogueModelIds] = useState<string[]>(initialModelImageUrl ? ['from-gallery'] : []);
  const [selectedCatalogueModelUrls, setSelectedCatalogueModelUrls] = useState<string[]>(initialModelImageUrl ? [initialModelImageUrl] : []);
  const [selectedCataloguePoses, setSelectedCataloguePoses] = useState<CataloguePoseData[]>([]);
  const [selectedBackgroundId, setSelectedBackgroundId] = useState<string | null>(initialBackgroundImageUrl ? 'from-gallery' : null);
  const [selectedBackgroundUrl, setSelectedBackgroundUrl] = useState<string | null>(initialBackgroundImageUrl ?? null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [examplePresetIdForSummary, setExamplePresetIdForSummary] = useState<number | null>(null);
  const { models, modelsLoading, posePresets, posesLoading, allBackgrounds, backgroundsLoading } = useWorkflowData();
  const [backgroundPresets, setBackgroundPresets] = useState<BackgroundPreset[]>([]);
  const [genderFilter, setGenderFilter] = useState<'female' | 'male'>('female');
  const [poseGenderFilter, setPoseGenderFilter] = useState<'female' | 'male'>('female');
  const [poseTypeFilter, setPoseTypeFilter] = useState('standing');
  const [poseSizeFilter, setPoseSizeFilter] = useState<'normal' | 'plus'>('normal');
  const [backgroundCategory, setBackgroundCategory] = useState('random');

  const [creditEstimation, estimateCreditCost] = useCreditEstimation('ai-workflow');
  const modeRestrictions = useModeRestrictions();

  const skipAutoNavRef = useRef(false);
  const prevModelRef = useRef<string | null>(null);
  const prevBackgroundRef = useRef<string | null>(null);

  const inputImageUrls = [frontImageUrl, backImageUrl].filter(Boolean);

  useEffect(() => {
    if (initialInputImages?.length) {
      if (initialInputImages[0]) setFrontImageUrl(initialInputImages[0]);
      if (initialInputImages[1]) setBackImageUrl(initialInputImages[1]);
    }
  }, [initialInputImages]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const numOutputImages =
      selectedCatalogueModelUrls.length > 0 && selectedCataloguePoses.length > 0
        ? Math.min(selectedCataloguePoses.length, 10)
        : 0;
    const singleModelUrl = selectedCatalogueModelUrls[0];
    const posesPayload = selectedCataloguePoses.slice(0, 10).map(p => ({ poseId: p.poseId, prompt: p.prompt, type: p.type, gender: p.gender }));
    estimateCreditCost({
      workflowType,
      inputImageUrls: inputImageUrls,
      userId: currentUser.uid,
      mode: mode.toLowerCase(),
      numImages: numOutputImages,
      ...(singleModelUrl && { modelImageUrl: singleModelUrl }),
      ...(posesPayload.length > 0 && { poses: posesPayload }),
      ...(prompt?.trim() && { userPrompt: prompt.trim() }),
    });
  }, [
    currentUser?.uid,
    workflowType,
    frontImageUrl,
    backImageUrl,
    selectedCatalogueModelUrls.length,
    selectedCataloguePoses.length,
    selectedBackgroundId,
    prompt,
    mode,
    estimateCreditCost,
  ]);

  useEffect(() => {
    if (backgroundCategory === 'random') {
      setBackgroundPresets(allBackgrounds);
    } else {
      setBackgroundPresets(allBackgrounds.filter((bg: BackgroundPreset) => bg.category.toLowerCase() === backgroundCategory));
    }
  }, [backgroundCategory, allBackgrounds]);

  useEffect(() => {
    if (skipAutoNavRef.current) return;
    if (selectedCatalogueModelIds.length === 0) {
      prevModelRef.current = null;
      return;
    }
    if (currentStep === 'SELECT_MODELS' && selectedCatalogueModelIds.length === 1) {
      const selectedId = selectedCatalogueModelIds[0];
      if (selectedId !== prevModelRef.current) {
        prevModelRef.current = selectedId;
        const t = setTimeout(() => setCurrentStep('SELECT_POSES'), 300);
        return () => clearTimeout(t);
      }
    }
  }, [currentStep, selectedCatalogueModelIds]);

  useEffect(() => {
    if (skipAutoNavRef.current) return;
    if (currentStep === 'SELECT_BACKGROUND' && selectedBackgroundId && selectedBackgroundId !== prevBackgroundRef.current) {
      prevBackgroundRef.current = selectedBackgroundId;
      const t = setTimeout(() => setCurrentStep('SUMMARY'), 300);
      return () => clearTimeout(t);
    }
  }, [currentStep, selectedBackgroundId]);

  useEffect(() => {
    if (skipAutoNavRef.current) {
      const t = setTimeout(() => { skipAutoNavRef.current = false; }, 100);
      return () => clearTimeout(t);
    }
  }, [currentStep]);

  const handleGenerate = async () => {
    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }
    setIsProcessing(true);
    try {
      const numImages = selectedCataloguePoses.length;
      const singleModelUrl = selectedCatalogueModelUrls[0];
      const payload: Record<string, unknown> = {
        inputImageUrls: [frontImageUrl, backImageUrl].filter(Boolean),
        userId: currentUser?.uid || 'anonymous',
        workflowType,
        mode: mode.toLowerCase(),
        numImages,
        imageSize: 'square',
        outputFormat: 'webp',
        enableSafetyChecker: true,
        poses: selectedCataloguePoses.map(p => ({ poseId: p.poseId, prompt: p.prompt, type: p.type, gender: p.gender })),
      };
      if (singleModelUrl) payload.modelImageUrl = singleModelUrl;
      if (selectedBackgroundUrl) payload.backgroundImageUrl = selectedBackgroundUrl;
      if (prompt?.trim()) payload.userPrompt = prompt.trim();

      const res = await fetch(API_CONFIG.AI_WORKFLOW_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setGeneratedImages(data.imageUrls);
        await refreshUserCredits();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsProcessing(false);
    }
  };

  const steps: { key: Step; label: string }[] = [
    { key: 'SELECT_PRODUCTS', label: 'Upload Images' },
    { key: 'SELECT_MODELS', label: 'Select Model' },
    { key: 'SELECT_POSES', label: 'Select Poses' },
    { key: 'SELECT_BACKGROUND', label: 'Select Background' },
    { key: 'SUMMARY', label: 'Summary' },
  ];

  const nextStep = () => {
    const idx = steps.findIndex(s => s.key === currentStep);
    if (currentStep === 'SELECT_PRODUCTS') {
      if (!frontImageUrl) {
        alert('Please upload at least the front image.');
        return;
      }
      if (backImageRequired && !backImageUrl) {
        alert('Please upload both front and back images.');
        return;
      }
    }
    if (currentStep === 'SELECT_MODELS') {
      if (selectedCatalogueModelIds.length !== 1) {
        alert('Please select one model.');
        return;
      }
    }
    if (currentStep === 'SELECT_POSES') {
      if (selectedCataloguePoses.length < 1 || selectedCataloguePoses.length > 10) {
        alert('Please select 1 to 10 poses.');
        return;
      }
    }
    if (idx < steps.length - 1) setCurrentStep(steps[idx + 1].key);
  };

  const prevStep = () => {
    const idx = steps.findIndex(s => s.key === currentStep);
    if (idx <= 0) {
      onBackToWorkflows();
      return;
    }
    skipAutoNavRef.current = true;
    setCurrentStep(steps[idx - 1].key);
  };

  const goToStep = (step: Step) => {
    const stepIdx = steps.findIndex(s => s.key === step);
    const currentIdx = steps.findIndex(s => s.key === currentStep);
    if (stepIdx >= 0 && stepIdx < currentIdx) {
      skipAutoNavRef.current = true;
      setCurrentStep(step);
    }
  };

  const handleStartNew = () => {
    setGeneratedImages([]);
    setFrontImageUrl('');
    setBackImageUrl('');
    setSelectedCatalogueModelIds([]);
    setSelectedCatalogueModelUrls([]);
    setSelectedCataloguePoses([]);
    setSelectedBackgroundId(null);
    setSelectedBackgroundUrl(null);
    setPrompt('');
    setCurrentStep('SELECT_PRODUCTS');
    onBackToWorkflows();
  };

  const handleDownloadResult = async (url: string, index: number) => {
    try {
      const r = await fetch(url);
      const blob = await r.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `${downloadFilenamePrefix}-${index + 1}.jpg`;
      a.click();
      window.URL.revokeObjectURL(u);
    } catch (err) {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${downloadFilenamePrefix}-${index + 1}.jpg`;
      a.click();
    }
  };

  const toggleModel = (model: ModelTemplate) => {
    const isSelected = selectedCatalogueModelIds.includes(model.id);
    if (isSelected) {
      setSelectedCatalogueModelIds([]);
      setSelectedCatalogueModelUrls([]);
    } else {
      setSelectedCatalogueModelIds([model.id]);
      setSelectedCatalogueModelUrls([model.imageUrl]);
    }
  };

  const togglePose = (pose: PosePreset) => {
    const isSelected = selectedCataloguePoses.some(p => p.poseId === pose.poseId);
    if (isSelected) {
      setSelectedCataloguePoses(prev => prev.filter(p => p.poseId !== pose.poseId));
    } else if (selectedCataloguePoses.length < 10) {
      setSelectedCataloguePoses(prev => [...prev, {
        poseId: pose.poseId,
        prompt: pose.prompt || '',
        type: pose.type || poseTypeFilter,
        gender: pose.gender || poseGenderFilter,
      }]);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <WorkflowStepper
        steps={steps}
        currentStep={currentStep}
        onStepClick={goToStep}
        onPrev={prevStep}
        onNext={currentStep !== 'SUMMARY' ? nextStep : undefined}
      />

      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-5">
          <div className={currentStep !== 'SUMMARY' ? 'lg:col-span-4' : 'lg:col-span-3'}>
            {currentStep === 'SELECT_PRODUCTS' && (
              <div className="flex flex-col items-center justify-center min-h-[400px] sm:min-h-[600px]">
                <div className="w-full">
                  <h3 className="text-xl sm:text-2xl font-bold text-center mb-2 text-gray-900">Upload Images to Begin</h3>
                  <p className="text-sm sm:text-base text-gray-500 text-center mb-6 sm:mb-12 px-2">Drag and drop an image here - PNG, JPG, WebP, AVIF or HEIC</p>
                  <div className="space-y-4 sm:space-y-8">
                    <div className="text-center">
                      <span className="inline-block bg-gray-100 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold text-gray-600 mb-4 sm:mb-6 uppercase tracking-wider">{workflowName}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 max-w-4xl mx-auto">
                      <ImageUpload imageUrls={frontImageUrl ? [frontImageUrl] : []} setImageUrls={(urls: any) => { const u = typeof urls === 'function' ? urls([]) : urls; setFrontImageUrl(u.length ? u[0] : ''); }} title="Front Image" uploadUrl={API_CONFIG.AI_WORKFLOW_UPLOAD_URL} />
                      <ImageUpload imageUrls={backImageUrl ? [backImageUrl] : []} setImageUrls={(urls: any) => { const u = typeof urls === 'function' ? urls([]) : urls; setBackImageUrl(u.length ? u[0] : ''); }} title={backImageLabel} uploadUrl={API_CONFIG.AI_WORKFLOW_UPLOAD_URL} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 'SELECT_MODELS' && (
              <ModelGrid
                models={models}
                loading={modelsLoading}
                selectedId={selectedCatalogueModelIds[0] || null}
                onSelect={toggleModel}
                genderFilter={genderFilter}
                onGenderChange={setGenderFilter}
              />
            )}

            {currentStep === 'SELECT_POSES' && (
              <PoseGrid
                poses={posePresets}
                loading={posesLoading}
                selectedPoses={selectedCataloguePoses}
                onTogglePose={togglePose}
                genderFilter={poseGenderFilter}
                onGenderChange={setPoseGenderFilter}
                typeFilter={poseTypeFilter}
                onTypeChange={setPoseTypeFilter}
                sizeFilter={poseSizeFilter}
                onSizeChange={setPoseSizeFilter}
              />
            )}

            {currentStep === 'SELECT_BACKGROUND' && (
              <BackgroundGrid
                backgrounds={backgroundPresets}
                loading={backgroundsLoading}
                selectedId={selectedBackgroundId}
                onSelect={(id, url) => {
                  setSelectedBackgroundId(id);
                  setSelectedBackgroundUrl(url);
                }}
                categories={BACKGROUND_CATEGORIES}
                currentCategory={backgroundCategory}
                onCategoryChange={setBackgroundCategory}
                showNoChangeOption={false}
              />
            )}

            {currentStep === 'SUMMARY' && (
              <div>
                {generatedImages.length > 0 ? (
                  <GeneratedResults
                    images={generatedImages}
                    onStartNew={handleStartNew}
                    onDownload={handleDownloadResult}
                    onNavigateToCanvas={(url, tool) => navigate('/canvas-editor', { state: { inputImages: [url], tool } })}
                  />
                ) : (
                  <div className="hidden lg:flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-200 shadow-sm" style={{ minHeight: 'calc(100vh - 220px)' }}>
                    <div className="text-center p-8">
                      <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6"><i className="fas fa-images text-gray-400 text-3xl"></i></div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">Ready to Generate</h3>
                      <p className="text-sm text-gray-500 max-w-md">Review your selections in the sidebar and click Generate.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {currentStep === 'SUMMARY' && (
            <div className="lg:col-span-1">
              <SummarySidebar
                inputImageUrls={inputImageUrls}
                selectedModelUrl={selectedCatalogueModelUrls[0]}
                selectedModelName={selectedCatalogueModelIds[0] ? (models.find((m: ModelTemplate) => m.id === selectedCatalogueModelIds[0])?.name ?? selectedCatalogueModelIds[0]) : selectedCatalogueModelUrls[0] ? 'Selected' : 'Not selected'}
                selectedBackgroundUrl={selectedBackgroundUrl}
                selectedBackgroundName={selectedBackgroundId && selectedBackgroundId !== 'from-gallery' ? selectedBackgroundId : selectedBackgroundUrl ? 'Selected' : 'Not selected'}
                selectedPoses={selectedCataloguePoses.map(p => ({ id: p.poseId, type: p.type, gender: p.gender }))}
                mode={mode}
                onModeChange={setMode}
                availableModes={modeRestrictions.availableModes}
                prompt={prompt}
                onPromptChange={setPrompt}
                showAdvancedSettings={showAdvancedSettings}
                onToggleAdvanced={() => setShowAdvancedSettings(!showAdvancedSettings)}
                creditEstimation={creditEstimation}
                onGenerate={handleGenerate}
                isProcessing={isProcessing}
              />
            </div>
          )}
        </div>
      </div>

      <InsufficientCreditsModal isOpen={showInsufficientCreditsModal} onClose={() => setShowInsufficientCreditsModal(false)} requiredCredits={creditEstimation.creditCost || 0} availableCredits={userCredits} featureName="AI Workflow" />
    </div>
  );
};

export default MultiModelPoseWorkflowBase;
